import { Money } from "@hubrise/react-components"

import { SkuId } from "../ids"
import { ICatalog, IDeal, IDealLine, IDealLineSku, IOptionList, ISku } from "../models"
import { OptionList, Sku } from "../utils"

import ItemCustomisation from "./ItemCustomisation"

type ItemDealLineSkus = {
  skuId: SkuId
  name: string
  extraCharge: Money | null
}

class ItemDealLine {
  private readonly catalog: ICatalog
  private readonly deal: IDeal
  private readonly dealLineIndex: number
  private dealLineSku: IDealLineSku
  private readonly customisation: ItemCustomisation

  constructor(catalog: ICatalog, deal: IDeal, dealLineIndex: number) {
    this.catalog = catalog
    this.deal = deal
    this.dealLineIndex = dealLineIndex
    this.dealLineSku = this.defaultDealLineSku
    this.customisation = new ItemCustomisation(catalog, this.sku)
  }

  get dealLine(): IDealLine {
    return this.deal.lines[this.dealLineIndex]
  }

  get sku(): ISku {
    return Sku.byId(this.catalog, this.dealLineSku.skuId)
  }

  get index(): number {
    return this.dealLineIndex
  }

  get label(): string {
    return this.dealLine.label
  }

  optionNames(optionListName: string): Array<string> {
    return this.customisation.optionNames(optionListName)
  }

  setOptionNames(optionListName: string, optionNames: Array<string>): void {
    this.customisation.setOptionNames(optionListName, optionNames)
  }

  get itemDealLineSkus(): Array<ItemDealLineSkus> {
    return this.dealLine.skus
      .map((dealLineSku) => {
        const { product, sku } = Sku.findWithProductById(this.catalog, dealLineSku.skuId)
        return {
          skuId: dealLineSku.skuId,
          name: product.name + (sku.name !== "" ? " - " + sku.name : ""),
          extraCharge: dealLineSku.extraCharge,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * Return the first deal line sku, as sorted by itemDealLineSkus.
   */
  get defaultDealLineSku(): IDealLineSku {
    const skuId = this.itemDealLineSkus[0].skuId
    return this.dealLine.skus.find((dealLineSku) => dealLineSku.skuId === skuId)!
  }

  price(variantRef: string | null): Money {
    return ItemDealLine.effect(this.dealLine, Sku.price(this.sku, variantRef))
      .plus(this.customisation.price(variantRef))
      .plus(this.dealLineSku.extraCharge || this.nullMoney)
  }

  get optionLists(): Array<IOptionList> {
    return OptionList.bySku(this.catalog, this.sku)
  }

  setSkuId(skuId: SkuId): void {
    this.dealLineSku = this.dealLine.skus.find((dealLineSku) => dealLineSku.skuId === skuId) || this.dealLine.skus[0]
    this.customisation.setSku(this.sku)
  }

  private static effect(dealLine: IDealLine, skuPrice: Money): Money {
    switch (dealLine.pricingEffect) {
      case "unchanged":
        return skuPrice
      case "fixed_price":
        return dealLine.pricingValue
      case "price_off":
        return skuPrice.plus(dealLine.pricingValue.times(-1))
      case "percentage_off":
        return skuPrice.times(1 - dealLine.pricingValue / 100)
    }
  }

  private get nullMoney(): Money {
    return Money.zero(this.catalog.currency)
  }
}

export default ItemDealLine
